import { PatientSummarizationStatusCard } from '@ctw/shared/content/summarization/patient-summarization-status-card';
import { PatientSummaryJobProvider } from '@ctw/shared/content/summarization/patient-summary-job-provider';
import type { UsePatientSummariesResponse } from '@ctw/shared/content/summarization/use-patient-summaries';
import { useUserPatientSummarizationContext } from '@ctw/shared/content/summarization/user-patient-summarization-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Encapsulates the summarization status card for the patient overview page.
 * Checks for a cached summary on mount and reads job state from the global
 * summarization context. Polling and toast notifications persist across
 * tab navigation within the patient since job state lives in the context.
 */
export function useOverviewSummarizationCard(): ReactNode {
  const hasSummarization = useFeatureFlag('ctw-hpi-standalone-zap');
  const hasSummarizationStreaming = useFeatureFlag('ctw-summarization-v2-ui-refresh');
  const { activeSummaryJob, generateSummary, checkCachedSummary } =
    useUserPatientSummarizationContext();
  const { patient } = usePatientContext();

  const patientUpid = patient.UPID;
  const isCurrentPatientJob =
    activeSummaryJob !== null &&
    patientUpid !== undefined &&
    activeSummaryJob.patientUpid === patientUpid;
  const cardResult: UsePatientSummariesResponse | null = useMemo(() => {
    if (!isCurrentPatientJob) {
      return null;
    }
    return {
      status: activeSummaryJob.status,
      summaries: [],
      submitJob: () => generateSummary(patientUpid ?? ''),
      reset: () => undefined,
      checkCachedSummary: async () => null,
    };
  }, [isCurrentPatientJob, activeSummaryJob, generateSummary, patientUpid]);

  const [isCheckingCache, setIsCheckingCache] = useState(false);

  const handleGenerate = useCallback(() => {
    if (patientUpid) {
      generateSummary(patientUpid);
    }
  }, [generateSummary, patientUpid]);

  // Check for a cached summary when opening a patient page.
  const cacheCheckedForUpidRef = useRef<string | null>(null);
  useEffect(() => {
    if (patientUpid && cacheCheckedForUpidRef.current !== patientUpid) {
      cacheCheckedForUpidRef.current = patientUpid;
      setIsCheckingCache(true);
      void checkCachedSummary(patientUpid).finally(() => {
        setIsCheckingCache(false);
      });
    }
  }, [patientUpid, checkCachedSummary]);

  const summaryResponseForProvider = useMemo<UsePatientSummariesResponse>(
    () =>
      cardResult ?? {
        status: 'error',
        summaries: [],
        submitJob: (upid: string) => generateSummary(upid),
        reset: () => undefined,
        checkCachedSummary: async () => null,
      },
    [cardResult, generateSummary],
  );

  if (!(hasSummarization && hasSummarizationStreaming)) {
    return null;
  }

  return (
    <PatientSummaryJobProvider summaryResponse={summaryResponseForProvider}>
      <PatientSummarizationStatusCard
        patientSummarizationResult={cardResult}
        onGenerateSummary={handleGenerate}
        isCheckingCache={isCheckingCache}
      />
    </PatientSummaryJobProvider>
  );
}
